import { useState, useRef, useEffect, useCallback } from "react";
import { toast } from "sonner";
import {
  Send, Mic, MicOff, FileText,
  Paperclip, CheckCircle2, Users, Clock,
  CalendarDays, ListChecks, Sparkles,
  ShieldAlert, Plus, BellRing, UserPlus, Ban, HelpCircle,
  BookOpen, MessageCircleQuestion, FolderInput, FileSpreadsheet,
  FileImage, FileType2, HardDriveUpload, ShieldCheck,
  Image, X, Lightbulb
} from "lucide-react";
import BetaFeedbackButton from "../../components/BetaFeedbackButton";
import { CalmModeModal } from "./CalmModeModal";
import { useCalmMode } from "./CalmModeContext";
import { WeeklyResetModal, useWeeklyReset } from "./WeeklyReset";
import {
  ResidenceLayout, PageHeading, TextLink, PillButton, Colophon, useLightPhase,
} from "./components/os/editorial";
import { ResidenceRail } from "./components/os/ResidenceRail";

/* ═══════════════════════════════════════════════
   CONSTANTS
   ═══════════════════════════════════════════════ */

const ACCEPTED_TYPES = ".pdf,.doc,.docx,.txt,.csv,.xlsx,.png,.jpg,.jpeg";

const WELCOME_MESSAGE = {
  id: "welcome",
  role: "assistant",
  text: "Kia ora. Tell me what needs sorting — meals, chores, school admin, money, appointments, files, routines, anything at all.",
  timestamp: new Date(),
};

const SUGGESTED_PROMPTS = [
  "Start Weekly Reset",
  "Plan our week",
  "Sort dinner from what we have",
  "Create a fair chore plan",
  "Summarise this school newsletter",
  "Put us in Calm Mode",
  "Prepare for tomorrow morning",
  "Create a shopping list",
  "Organise this uploaded file",
];

/* ═══════════════════════════════════════════════
   APPROVAL KEYWORD MATCHING
   Determines whether ARA needs human approval before acting.
   Triggers on: spending, booking, cancelling, health, school, sharing docs.
   ═══════════════════════════════════════════════ */

const APPROVAL_KEYWORDS = {
  money:   ["buy", "purchase", "spend", "pay", "order", "$", "cost", "price", "subscribe", "renew", "invoice"],
  booking: ["book", "schedule", "reserve", "appointment", "rsvp"],
  cancel:  ["cancel", "unsubscribe", "refund"],
  health:  ["doctor", "gp", "medical", "health", "prescription", "dentist", "nurse", "hospital", "vaccine", "physio"],
  school:  ["school", "teacher", "permission slip", "newsletter", "principal", "homework"],
  share:   ["share", "send", "email", "forward", "attach", "upload to", "send to"],
  docs:    ["document", "contract", "ird", "passport", "id card", "licence", "license", "tax", "lease"],
};

function detectApprovalReasons(text) {
  const lower = (text || "").toLowerCase();
  const reasons = [];
  for (const [reason, words] of Object.entries(APPROVAL_KEYWORDS)) {
    if (words.some(w => lower.includes(w))) reasons.push(reason);
  }
  return reasons;
}

const REASON_LABELS = {
  money:   "spending money",
  booking: "making a booking",
  cancel:  "cancelling something",
  health:  "a health matter",
  school:  "school-related action",
  share:   "sharing information",
  docs:    "personal documents",
};

/* ═══════════════════════════════════════════════
   MOCK RESPONSE GENERATOR — structured, kid/parent friendly
   Each action has: what, who, when, why, status, approvalNeeded
   ═══════════════════════════════════════════════ */

const STATUS_AI_SUGGESTED = "Ara suggested";
const STATUS_NEEDS_ACTION = "Needs action";
const STATUS_AWAITING_APPROVAL = "Awaiting approval";
const STATUS_READY = "Ready to schedule";

function generateMockResponse(userText, files) {
  const lower = (userText || "").toLowerCase();
  const hasFiles = files && files.length > 0;
  const approvalReasons = detectApprovalReasons(userText);
  const approvalRequired = approvalReasons.length > 0;

  // ───── CALM MODE
  if (lower.includes("calm")) {
    return buildResponse({
      summary: "You'd like to dial the household down to Calm Mode for tonight.",
      actions: [
        { what: "Pause all non-urgent chore reminders", who: "ARA", when: "Now",
          why: "Reduces noise so the family can wind down.", status: STATUS_AI_SUGGESTED, approvalNeeded: false },
        { what: "Swap tonight's dinner for something easy (toasties or wraps)", who: "Mum or Dad", when: "5:30 PM",
          why: "Less prep means less stress.", status: STATUS_NEEDS_ACTION, approvalNeeded: false },
        { what: "Push school prep tasks to the morning", who: "Kids", when: "Tomorrow 7:00 AM",
          why: "Frees up the evening for rest.", status: STATUS_AI_SUGGESTED, approvalNeeded: false },
        { what: "Start wind-down routine 30 mins earlier", who: "Everyone", when: "7:30 PM",
          why: "Earlier wind-down helps sleep quality.", status: STATUS_READY, approvalNeeded: false },
      ],
      approvalRequired, approvalReasons,
    });
  }

  // ───── DINNER / MEAL
  if (lower.includes("dinner") || lower.includes("meal") || lower.includes("cook") || lower.includes("food")) {
    // Pull ingredient hints from the user's text
    const ingredients = ["mince", "rice", "broccoli", "chicken", "pasta", "potato", "egg", "lettuce", "salsa", "cheese"]
      .filter(i => lower.includes(i));
    const ingredientLabel = ingredients.length ? ingredients.join(", ") : "what's already in the fridge";
    return buildResponse({
      summary: `You need a quick dinner plan using ${ingredientLabel}.`,
      actions: [
        { what: ingredients.length
            ? `Cook ${ingredients.slice(0,2).join(" & ")} bowls`
            : "Cook a one-pan family dinner",
          who: "Mum or Teen", when: "5:30 PM",
          why: "Uses food already available and keeps dinner simple.",
          status: STATUS_AI_SUGGESTED, approvalNeeded: false },
        { what: "Defrost protein from the freezer", who: "Dad", when: "Before 3:00 PM",
          why: "Dinner will be faster later.", status: STATUS_NEEDS_ACTION, approvalNeeded: false },
        { what: "Add sauce + sides to the shopping list", who: "ARA", when: "Today",
          why: "Completes the meal so nothing's missing.",
          status: STATUS_AI_SUGGESTED, approvalNeeded: true },
        { what: "Set the table and pour drinks", who: "Younger child", when: "6:00 PM",
          why: "Small wins, kid involvement, family ritual.",
          status: STATUS_READY, approvalNeeded: false },
      ],
      approvalRequired, approvalReasons,
    });
  }

  // ───── CHORES
  if (lower.includes("chore")) {
    return buildResponse({
      summary: "You want a fair chore plan for the week.",
      actions: [
        { what: "Empty dishwasher + wipe down the bench", who: "Teen", when: "After school weekdays",
          why: "Fits naturally into their afternoon routine.",
          status: STATUS_AI_SUGGESTED, approvalNeeded: false },
        { what: "Take out the bins and recycling", who: "Younger child", when: "Tuesday & Friday evenings",
          why: "Light task, good for building responsibility.",
          status: STATUS_AI_SUGGESTED, approvalNeeded: false },
        { what: "Vacuum lounge and hallway", who: "Dad", when: "Saturday morning",
          why: "Keeps weekday energy lower.",
          status: STATUS_NEEDS_ACTION, approvalNeeded: false },
        { what: "Sort & fold the laundry", who: "Mum + Teen", when: "Sunday afternoon",
          why: "Splitting the load makes it manageable.",
          status: STATUS_AI_SUGGESTED, approvalNeeded: false },
        { what: "Feed the cat", who: "Younger child", when: "Morning + evening",
          why: "Daily routine to build consistency.",
          status: STATUS_READY, approvalNeeded: false },
      ],
      approvalRequired, approvalReasons,
    });
  }

  // ───── WEEK PLAN
  if (lower.includes("week") || lower.includes("plan")) {
    return buildResponse({
      summary: "You'd like a weekly household plan drafted.",
      actions: [
        { what: "Confirm meal plan for Mon–Fri", who: "Mum", when: "Sunday evening",
          why: "Locks dinners in early so shopping is accurate.",
          status: STATUS_AI_SUGGESTED, approvalNeeded: false },
        { what: "Place grocery order online", who: "Dad", when: "Monday 9:00 AM",
          why: "Delivers in time for the school week.",
          status: STATUS_NEEDS_ACTION, approvalNeeded: true },
        { what: "Batch-prep school lunches", who: "Teen + Mum", when: "Sunday + Wednesday",
          why: "Halves the weekday morning load.",
          status: STATUS_AI_SUGGESTED, approvalNeeded: false },
        { what: "Mid-week chore check-in", who: "Whole family", when: "Wednesday 6:00 PM",
          why: "Catches gaps before they pile up.",
          status: STATUS_READY, approvalNeeded: false },
        { what: "Confirm weekend activities (netball + family time)", who: "Dad", when: "Friday evening",
          why: "Avoids last-minute scrambles.",
          status: STATUS_AI_SUGGESTED, approvalNeeded: false },
      ],
      approvalRequired, approvalReasons,
    });
  }

  // ───── SHOPPING / GROCERIES
  if (lower.includes("shopping") || lower.includes("list") || lower.includes("grocer")) {
    return buildResponse({
      summary: "You want a shopping list built from your meal plan and low-stock items.",
      actions: [
        { what: "Add: Milk (2L), bread, bananas, chicken breast", who: "ARA", when: "Now",
          why: "Covers the basics for the week's meals.",
          status: STATUS_AI_SUGGESTED, approvalNeeded: false },
        { what: "Add: dishwasher tablets, bin liners", who: "ARA", when: "Now",
          why: "Stock is running low at home.",
          status: STATUS_AI_SUGGESTED, approvalNeeded: false },
        { what: "Add: Lily's art supplies for school project", who: "Mum", when: "Before Thursday",
          why: "Project is due Friday.",
          status: STATUS_NEEDS_ACTION, approvalNeeded: true },
        { what: "Place Countdown online order", who: "Dad", when: "Tomorrow morning",
          why: "Delivery lands before dinner.",
          status: STATUS_AWAITING_APPROVAL, approvalNeeded: true },
      ],
      approvalRequired, approvalReasons,
    });
  }

  // ───── TOMORROW / MORNING PREP
  if (lower.includes("tomorrow") || lower.includes("morning")) {
    return buildResponse({
      summary: "You want tomorrow morning to run smoothly.",
      actions: [
        { what: "Set school bags by the door", who: "Kids", when: "Tonight, 8:00 PM",
          why: "No frantic hunting in the morning.",
          status: STATUS_AI_SUGGESTED, approvalNeeded: false },
        { what: "Prep packed lunches (wraps + fruit)", who: "Mum or Teen", when: "Tonight 8:30 PM",
          why: "One less thing in the morning rush.",
          status: STATUS_NEEDS_ACTION, approvalNeeded: false },
        { what: "Confirm Max's 8:30 AM dentist appointment", who: "Dad", when: "Tonight",
          why: "Avoids a missed booking.",
          status: STATUS_AWAITING_APPROVAL, approvalNeeded: true },
        { what: "Set alarms — kids 6:45 AM, adults 6:30 AM", who: "ARA", when: "Auto",
          why: "Keeps everyone on track.",
          status: STATUS_READY, approvalNeeded: false },
      ],
      approvalRequired, approvalReasons,
    });
  }

  // ───── SCHOOL / NEWSLETTER
  if (lower.includes("school") || lower.includes("newsletter") || lower.includes("summar")) {
    const fileNote = hasFiles ? `From the ${files.length} file${files.length > 1 ? 's' : ''} you shared, ` : "";
    return buildResponse({
      summary: `${fileNote}you want the key school actions pulled out.`,
      actions: [
        { what: "Mufti day Friday — gold coin donation", who: "Both kids", when: "Friday morning",
          why: "Don't want them to miss out.",
          status: STATUS_AI_SUGGESTED, approvalNeeded: false },
        { what: "Book parent–teacher conference slot", who: "Mum or Dad", when: "By Wednesday",
          why: "Slots fill up fast.",
          status: STATUS_AWAITING_APPROVAL, approvalNeeded: true },
        { what: "Return school trip permission slip", who: "Max", when: "Thursday",
          why: "Required to attend.",
          status: STATUS_NEEDS_ACTION, approvalNeeded: true },
        { what: "Add all dates to family calendar", who: "ARA", when: "Now",
          why: "Keeps everything in one place.",
          status: STATUS_AI_SUGGESTED, approvalNeeded: false },
      ],
      approvalRequired, approvalReasons,
    });
  }

  // ───── FILE UPLOAD
  if (hasFiles && (lower.includes("file") || lower.includes("organise") || lower.includes("upload"))) {
    return buildResponse({
      summary: `You've shared ${files.length} file${files.length > 1 ? 's' : ''} and want them organised.`,
      actions: [
        { what: `Categorise "${files[0].name}"`, who: "ARA", when: "Now",
          why: "Routes it to the right module (School, Admin, Health).",
          status: STATUS_AI_SUGGESTED, approvalNeeded: false },
        { what: "Extract dates, deadlines and action items", who: "ARA", when: "Now",
          why: "Surfaces anything time-sensitive.",
          status: STATUS_AI_SUGGESTED, approvalNeeded: false },
        { what: "Add extracted events to the family calendar", who: "ARA", when: "After your review",
          why: "Keeps everyone in sync.",
          status: STATUS_AWAITING_APPROVAL, approvalNeeded: true },
        { what: "Store in the Documents module under the right tag", who: "ARA", when: "After approval",
          why: "Easy to find later.",
          status: STATUS_AWAITING_APPROVAL, approvalNeeded: true },
      ],
      approvalRequired: approvalRequired || true,
      approvalReasons: approvalReasons.includes("docs") ? approvalReasons : [...approvalReasons, "docs"],
    });
  }

  // ───── DEFAULT FALLBACK
  return buildResponse({
    summary: "I've turned your instruction into a household action plan.",
    actions: [
      { what: "Review and confirm the details with the family", who: "You", when: "Now",
        why: "Makes sure we capture the right priorities.",
        status: STATUS_NEEDS_ACTION, approvalNeeded: false },
      { what: "Create tasks from this instruction", who: "ARA", when: "After your approval",
        why: "Gets it into the household timeline.",
        status: STATUS_AI_SUGGESTED, approvalNeeded: false },
      { what: "Assign to the right family member", who: "ARA", when: "After approval",
        why: "Spreads the load fairly.",
        status: STATUS_AI_SUGGESTED, approvalNeeded: false },
      { what: "Set reminders and follow-ups", who: "ARA", when: "Automatic",
        why: "Nothing slips through the cracks.",
        status: STATUS_READY, approvalNeeded: false },
    ],
    approvalRequired, approvalReasons,
  });
}

function buildResponse({ summary, actions, approvalRequired, approvalReasons }) {
  const approvalMessage = approvalRequired
    ? `Approval needed before ARA takes action — this involves ${
        approvalReasons.map(r => REASON_LABELS[r]).filter(Boolean).join(", ")
      }.`
    : "ARA can go ahead with the suggestions you approve below.";
  return { summary, actions, approvalRequired: !!approvalRequired, approvalMessage };
}

/* ═══════════════════════════════════════════════
   FILE CHIP
   ═══════════════════════════════════════════════ */

function FileChip({ file, onRemove }) {
  const isImage = /\.(png|jpg|jpeg)$/i.test(file.name);
  return (
    <div className="inline-flex items-center gap-2 bg-gray-100 dark:bg-slate-700 rounded-xl px-3 py-1.5 text-xs font-bold text-gray-700 dark:text-gray-200" data-testid={`file-chip-${file.name}`}>
      {isImage ? <Image className="w-3.5 h-3.5 text-blue-500" strokeWidth={1.5} /> : <FileText className="w-3.5 h-3.5 text-violet-500" strokeWidth={1.5} />}
      <span className="truncate max-w-[140px]">{file.name}</span>
      {onRemove && (
        <button onClick={() => onRemove(file.name)} className="hover:text-rose-500 transition-colors" data-testid={`remove-file-${file.name}`}>
          <X className="w-3.5 h-3.5" strokeWidth={1.5} />
        </button>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════
   FILE HELPERS & ATTACHMENT CARD
   Used in the composer preview before sending.
   The card surfaces what ARA *will* be able to do once
   backend parsing is connected. It never claims the file
   has already been read.
   ═══════════════════════════════════════════════ */

function formatFileSize(bytes) {
  if (!bytes || bytes <= 0) return null;
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb < 10 ? kb.toFixed(1) : Math.round(kb)} KB`;
  const mb = kb / 1024;
  return `${mb.toFixed(1)} MB`;
}

function getFileMeta(file) {
  const name = file.name || "Untitled file";
  const lower = name.toLowerCase();
  if (/\.(png|jpg|jpeg|webp|gif|heic|heif)$/i.test(lower)) {
    return { label: "Image", Icon: FileImage, accent: "bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-300 border-blue-200 dark:border-blue-700/60" };
  }
  if (lower.endsWith(".pdf")) {
    return { label: "PDF document", Icon: FileType2, accent: "bg-rose-50 dark:bg-rose-900/30 text-rose-600 dark:text-rose-300 border-rose-200 dark:border-rose-700/60" };
  }
  if (/\.(doc|docx)$/i.test(lower)) {
    return { label: "Word document", Icon: FileText, accent: "bg-sky-50 dark:bg-sky-900/30 text-sky-600 dark:text-sky-300 border-sky-200 dark:border-sky-700/60" };
  }
  if (/\.(xls|xlsx|csv)$/i.test(lower)) {
    return { label: "Spreadsheet", Icon: FileSpreadsheet, accent: "bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-300 border-emerald-200 dark:border-emerald-700/60" };
  }
  if (lower.endsWith(".txt")) {
    return { label: "Text file", Icon: FileText, accent: "bg-gray-100 dark:bg-slate-800 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-slate-700" };
  }
  return { label: file.type || "File", Icon: FileText, accent: "bg-violet-50 dark:bg-violet-900/30 text-violet-600 dark:text-violet-300 border-violet-200 dark:border-violet-700/60" };
}

const FILE_ACTIONS = [
  { key: "summarise",   label: "Summarise file",          icon: BookOpen },
  { key: "dates",       label: "Extract dates",           icon: CalendarDays },
  { key: "tasks",       label: "Create tasks",            icon: ListChecks },
  { key: "documents",   label: "Add to Documents",        icon: FolderInput },
  { key: "ask",         label: "Ask a question",          icon: MessageCircleQuestion },
];

function buildFileActionResponse(file, actionKey) {
  const name = file?.name || "this file";
  const safeName = name.length > 48 ? `${name.slice(0, 45)}…` : name;
  const base = {
    summary: "",
    actions: [],
    approvalRequired: false,
    approvalMessage: "This is staged for backend integration — no real parsing has happened yet.",
  };
  switch (actionKey) {
    case "summarise":
      return {
        ...base,
        summary: `I'll be able to give you a plain-English summary of "${safeName}" once file parsing is connected. The UI is ready — backend integration is the next step.`,
        actions: [
          { what: "Generate a 3-line plain-English summary", who: "ARA", when: "Once parsing is live", why: "So you can scan the file in seconds.", status: "Ara suggested", approvalNeeded: false },
          { what: "Flag anything time-sensitive or money-related", who: "ARA", when: "After summary", why: "So nothing important slips through.", status: "Ara suggested", approvalNeeded: false },
        ],
      };
    case "dates":
      return {
        ...base,
        summary: `I can help turn "${safeName}" into calendar events and reminders. Full file parsing will be connected in a later build. For now, this is ready for backend integration.`,
        actions: [
          { what: "Extract every date, deadline and event", who: "ARA", when: "Once parsing is live", why: "Turns a static file into actionable diary entries.", status: "Ara suggested", approvalNeeded: false },
          { what: "Add events to the family calendar", who: "ARA", when: "After your review", why: "Keeps the whole household aligned.", status: "Awaiting approval", approvalNeeded: true },
        ],
        approvalRequired: true,
        approvalMessage: "Approval needed before ARA takes action — adding events to the family calendar.",
      };
    case "tasks":
      return {
        ...base,
        summary: `I'll be able to pull tasks (with who/when/why) out of "${safeName}" once parsing is connected. The structure is ready — backend parsing is the next step.`,
        actions: [
          { what: "Identify any actions, asks or to-dos in the file", who: "ARA", when: "Once parsing is live", why: "Avoids missing requests buried in a long document.", status: "Ara suggested", approvalNeeded: false },
          { what: "Suggest who should own each task", who: "ARA", when: "After extraction", why: "Spreads the load fairly across the family.", status: "Ara suggested", approvalNeeded: false },
        ],
      };
    case "documents":
      return {
        ...base,
        summary: `I'll file "${safeName}" under the right Documents category once parsing is connected. This action is staged for backend integration.`,
        actions: [
          { what: "Categorise into School, Admin, Health, Finance or Other", who: "ARA", when: "Once parsing is live", why: "Easy to find later — no folder hunting.", status: "Ara suggested", approvalNeeded: false },
          { what: "Store securely in the household Documents module", who: "ARA", when: "After your approval", why: "Keeps the original copy safe and shareable.", status: "Awaiting approval", approvalNeeded: true },
        ],
        approvalRequired: true,
        approvalMessage: "Approval needed before ARA takes action — this involves personal documents.",
      };
    case "ask":
      return {
        ...base,
        summary: `Ask me anything about "${safeName}". Once file parsing is connected, I'll be able to answer from the file's contents. For now, this is ready for backend integration.`,
        actions: [
          { what: "Type your question in the chat below", who: "You", when: "Now", why: "I'll capture it so it's ready for the parsed answer.", status: "Needs action", approvalNeeded: false },
          { what: "Answer using the file's contents", who: "ARA", when: "Once parsing is live", why: "Saves you reading through long documents.", status: "Ara suggested", approvalNeeded: false },
        ],
      };
    default:
      return { ...base, summary: `Action staged for "${safeName}".` };
  }
}

function FileAttachmentCard({ file, onRemove, onAction }) {
  const meta = getFileMeta(file);
  const sizeLabel = formatFileSize(file.size);
  const { Icon } = meta;
  return (
    <div
      className="rounded-2xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900/60 p-3.5"
      data-testid={`file-attachment-${file.name}`}
    >
      <div className="flex items-start gap-3">
        {/* Icon tile */}
        <div className={`w-11 h-11 rounded-2xl border flex items-center justify-center flex-shrink-0 ${meta.accent}`}>
          <Icon className="w-5 h-5" strokeWidth={1.5} />
        </div>

        {/* Name + meta */}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-extrabold text-gray-900 dark:text-gray-50 truncate" title={file.name}>
            {file.name}
          </p>
          <div className="flex items-center flex-wrap gap-x-2 gap-y-0.5 mt-0.5">
            <span className="text-[11px] font-bold text-gray-500 dark:text-gray-400">{meta.label}</span>
            {sizeLabel && (
              <>
                <span className="text-[11px] text-gray-300 dark:text-gray-600">•</span>
                <span className="text-[11px] font-bold text-gray-500 dark:text-gray-400">{sizeLabel}</span>
              </>
            )}
            <span className="text-[11px] text-gray-300 dark:text-gray-600">•</span>
            <span className="inline-flex items-center gap-1 text-[10px] font-extrabold uppercase tracking-wide text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-700/60 rounded-full px-1.5 py-0.5">
              <ShieldCheck className="w-2.5 h-2.5" strokeWidth={1.5} />
              Awaiting review
            </span>
          </div>
        </div>

        {/* Remove */}
        <button
          onClick={() => onRemove(file.name)}
          className="flex-shrink-0 w-7 h-7 rounded-full text-gray-400 hover:text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-900/20 flex items-center justify-center transition-colors"
          title="Remove file"
          data-testid={`remove-file-${file.name}`}
        >
          <X className="w-4 h-4" strokeWidth={1.5} />
        </button>
      </div>

      {/* Suggested actions */}
      <div className="mt-3 pt-3 border-t border-gray-100 dark:border-slate-800">
        <p className="text-[10px] font-extrabold uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-2">
          Suggested actions
        </p>
        <div className="flex flex-wrap gap-1.5">
          {FILE_ACTIONS.map(act => {
            const ActIcon = act.icon;
            return (
              <button
                key={act.key}
                onClick={() => onAction(file, act.key, act.label)}
                className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-2.5 py-1.5 text-[11px] font-bold text-gray-700 dark:text-gray-200 hover:border-violet-300 dark:hover:border-violet-600 hover:text-violet-700 dark:hover:text-violet-300 transition-all duration-150 active:scale-95"
                data-testid={`file-action-${act.key}-${file.name}`}
              >
                <ActIcon className="w-3 h-3" strokeWidth={1.5} />
                {act.label}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════
   ACTION CARD — one suggested household action
   ═══════════════════════════════════════════════ */

const STATUS_STYLES = {
  "Ara suggested":       "bg-violet-50 dark:bg-violet-900/30 text-violet-700 dark:text-violet-200 border-violet-200 dark:border-violet-700/60",
  "Needs action":       "bg-rose-50 dark:bg-rose-900/30 text-rose-700 dark:text-rose-200 border-rose-200 dark:border-rose-700/60",
  "Awaiting approval":  "bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-200 border-amber-200 dark:border-amber-700/60",
  "Ready to schedule":  "bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-200 border-emerald-200 dark:border-emerald-700/60",
};

function ActionCard({ action, index, messageId }) {
  const [resolved, setResolved] = useState(null); // null | "task" | "reminder" | "person" | "ignored"

  const baseTestId = `action-${messageId}-${index}`;
  const statusClass = STATUS_STYLES[action.status] || STATUS_STYLES["Ara suggested"];

  const handleClick = (type, label) => {
    setResolved(type);
    toast.success(`${label} — "${action.what.slice(0, 60)}${action.what.length > 60 ? "…" : ""}"`);
    // eslint-disable-next-line no-console
    console.log("[ARA mock]", type, action);
  };

  return (
    <div
      className={`rounded-2xl border bg-white dark:bg-slate-900/60 border-gray-200 dark:border-slate-700 p-3.5 ${
        resolved === "ignored" ? "opacity-50" : ""
      }`}
      data-testid={baseTestId}
    >
      {/* Top row: number + What + status pill */}
      <div className="flex items-start gap-2.5 mb-2.5">
        <span className="text-[11px] font-extrabold text-gray-400 dark:text-gray-500 w-5 mt-0.5">{String(index + 1).padStart(2, "0")}</span>
        <p className="flex-1 text-sm font-bold text-gray-900 dark:text-gray-50 leading-snug">{action.what}</p>
        <span className={`text-[10px] font-extrabold uppercase tracking-wide px-2 py-0.5 rounded-full border whitespace-nowrap ${statusClass}`} data-testid={`${baseTestId}-status`}>
          {action.status}
        </span>
      </div>

      {/* Metadata grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5 pl-7 mb-3">
        <Meta icon={Users} label="Who" value={action.who} />
        <Meta icon={Clock} label="When" value={action.when} />
        <Meta icon={HelpCircle} label="Why" value={action.why} fullWidth />
        <Meta
          icon={ShieldAlert}
          label="Approval"
          value={action.approvalNeeded ? "Yes" : "No"}
          accent={action.approvalNeeded ? "amber" : "emerald"}
          testId={`${baseTestId}-approval`}
        />
      </div>

      {/* Action buttons */}
      <div className="flex flex-wrap gap-1.5 pl-7">
        <ActionBtn
          icon={Plus} label="Add task"
          onClick={() => handleClick("task", "Added to tasks")}
          active={resolved === "task"}
          testId={`${baseTestId}-add-task`}
          disabled={resolved === "ignored"}
        />
        <ActionBtn
          icon={BellRing} label="Add reminder"
          onClick={() => handleClick("reminder", "Reminder set")}
          active={resolved === "reminder"}
          testId={`${baseTestId}-add-reminder`}
          disabled={resolved === "ignored"}
        />
        <ActionBtn
          icon={UserPlus} label="Assign person"
          onClick={() => handleClick("person", "Person assigned")}
          active={resolved === "person"}
          testId={`${baseTestId}-assign-person`}
          disabled={resolved === "ignored"}
        />
        <ActionBtn
          icon={Ban} label="Ignore"
          onClick={() => handleClick("ignored", "Ignored")}
          active={resolved === "ignored"}
          testId={`${baseTestId}-ignore`}
          variant="ghost"
        />
      </div>
    </div>
  );
}

function Meta({ icon: Icon, label, value, fullWidth, accent, testId }) {
  const valueClass = accent === "amber"
    ? "text-amber-700 dark:text-amber-300 font-extrabold"
    : accent === "emerald"
      ? "text-emerald-700 dark:text-emerald-300 font-extrabold"
      : "text-gray-700 dark:text-gray-200 font-semibold";
  return (
    <div className={`flex items-start gap-1.5 ${fullWidth ? "sm:col-span-2" : ""}`} data-testid={testId}>
      <Icon className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500 mt-0.5 flex-shrink-0" strokeWidth={1.5} />
      <p className="text-[11px] leading-snug">
        <span className="text-gray-400 dark:text-gray-500 font-bold uppercase tracking-wide mr-1">{label}:</span>
        <span className={valueClass}>{value}</span>
      </p>
    </div>
  );
}

function ActionBtn({ icon: Icon, label, onClick, active, disabled, testId, variant }) {
  const base = "inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-[11px] font-bold border transition-all duration-150 active:scale-95";
  const stateClass = active
    ? "bg-[#1a4d3e] text-white border-[#1a4d3e]"
    : variant === "ghost"
      ? "bg-transparent text-gray-500 dark:text-gray-400 border-transparent hover:bg-gray-100 dark:hover:bg-slate-800"
      : "bg-white dark:bg-slate-800 text-gray-700 dark:text-gray-200 border-gray-200 dark:border-slate-700 hover:border-violet-300 dark:hover:border-violet-600 hover:text-violet-700 dark:hover:text-violet-300";
  return (
    <button
      onClick={onClick}
      disabled={disabled && !active}
      className={`${base} ${stateClass} ${disabled && !active ? "opacity-40 cursor-not-allowed" : ""}`}
      data-testid={testId}
    >
      <Icon className="w-3 h-3" strokeWidth={1.5} />{label}
    </button>
  );
}

/* ═══════════════════════════════════════════════
   MESSAGE BUBBLE
   ═══════════════════════════════════════════════ */

function MessageBubble({ message }) {
  const isUser = message.role === "user";

  return (
    <div className={`flex gap-3 ${isUser ? "flex-row-reverse" : ""}`} data-testid={`message-${message.id}`}>
      {/* Avatar */}
      {!isUser && (
        <div className="w-9 h-9 rounded-2xl bg-gradient-to-br  to-fuchsia-100 dark: dark:to-fuchsia-900/30 flex items-center justify-center flex-shrink-0 mt-1">
          <Sparkles className="w-4.5 h-4.5 text-violet-600 dark:text-violet-300" strokeWidth={1.5} />
        </div>
      )}

      {/* Bubble */}
      <div className={`${isUser ? "max-w-[85%] sm:max-w-[75%] ml-auto" : "max-w-[92%] sm:max-w-[88%]"}`}>
        <div className={`rounded-[20px] px-5 py-4 ${
          isUser
            ? "bg-[#1a4d3e] text-white rounded-br-lg"
            : "bg-white dark:bg-slate-800 border border-gray-100 dark:border-slate-700 text-gray-800 dark:text-gray-100 rounded-bl-lg"
        }`}>
          {/* Plain text (suppressed for assistant when structured response replaces it) */}
          {message.text && !message.response && (
            <p className="text-sm leading-relaxed whitespace-pre-wrap">{message.text}</p>
          )}

          {/* Structured response */}
          {message.response && (
            <div className="mt-4 space-y-4">
              {/* Summary */}
              <div className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" strokeWidth={1.5} />
                <div>
                  <p className="text-[11px] font-extrabold uppercase tracking-wider text-emerald-700 dark:text-emerald-300 mb-1">Summary</p>
                  <p className="text-sm font-medium leading-relaxed">{message.response.summary}</p>
                </div>
              </div>

              {/* Global approval banner */}
              {message.response.approvalRequired && (
                <div
                  className="flex items-start gap-2 rounded-2xl border border-amber-200 dark:border-amber-700/60 bg-amber-50 dark:bg-amber-900/20 px-3.5 py-3"
                  data-testid={`approval-banner-${message.id}`}
                >
                  <ShieldAlert className="w-4 h-4 text-amber-600 dark:text-amber-300 mt-0.5 flex-shrink-0" strokeWidth={1.5} />
                  <div>
                    <p className="text-[11px] font-extrabold uppercase tracking-wider text-amber-700 dark:text-amber-300 mb-0.5">Approval needed</p>
                    <p className="text-sm font-medium text-amber-900 dark:text-amber-100 leading-snug">
                      {message.response.approvalMessage}
                    </p>
                  </div>
                </div>
              )}

              {/* Action cards */}
              <div>
                <p className="text-[11px] font-extrabold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2 flex items-center gap-1.5">
                  <Lightbulb className="w-3.5 h-3.5" strokeWidth={1.5} />
                  Suggested household actions
                </p>
                <div className="space-y-2.5">
                  {message.response.actions.map((action, idx) => (
                    <ActionCard
                      key={idx}
                      action={action}
                      index={idx}
                      messageId={message.id}
                    />
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Attached files */}
        {message.files && message.files.length > 0 && (
          <div className={`flex flex-wrap gap-1.5 mt-2 ${isUser ? "justify-end" : ""}`}>
            {message.files.map(f => <FileChip key={f.name} file={f} />)}
          </div>
        )}

        {/* Timestamp */}
        <p className={`text-[10px] text-gray-400 mt-1.5 ${isUser ? "text-right" : ""}`}>
          {message.timestamp.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
        </p>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════
   TYPING INDICATOR
   ═══════════════════════════════════════════════ */

function TypingIndicator() {
  return (
    <div className="flex gap-3" data-testid="typing-indicator">
      <div className="w-9 h-9 rounded-2xl bg-gradient-to-br  to-fuchsia-100 dark: dark:to-fuchsia-900/30 flex items-center justify-center flex-shrink-0">
        <Sparkles className="w-4.5 h-4.5 text-violet-600 dark:text-violet-300" strokeWidth={1.5} />
      </div>
      <div className="bg-white dark:bg-slate-800 border border-gray-100 dark:border-slate-700 rounded-[20px] rounded-bl-lg px-5 py-4">
        <div className="flex items-center gap-1.5">
          <div className="w-2 h-2 rounded-full bg-gray-300 dark:bg-gray-600 animate-bounce" style={{ animationDelay: "0ms" }} />
          <div className="w-2 h-2 rounded-full bg-gray-300 dark:bg-gray-600 animate-bounce" style={{ animationDelay: "150ms" }} />
          <div className="w-2 h-2 rounded-full bg-gray-300 dark:bg-gray-600 animate-bounce" style={{ animationDelay: "300ms" }} />
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════
   MAIN PAGE
   ═══════════════════════════════════════════════ */

export default function AICommandCentrePage() {
  const [messages, setMessages] = useState([WELCOME_MESSAGE]);
  const [inputText, setInputText] = useState("");
  const [attachedFiles, setAttachedFiles] = useState([]);
  const [isTyping, setIsTyping] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [showPrompts, setShowPrompts] = useState(true);
  const [voiceSupported, setVoiceSupported] = useState(false);
  const [interimTranscript, setInterimTranscript] = useState("");

  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const textareaRef = useRef(null);
  const recognitionRef = useRef(null);

  // Detect browser speech recognition support once on mount.
  useEffect(() => {
    const SR = typeof window !== "undefined" && (window.SpeechRecognition || window.webkitSpeechRecognition);
    setVoiceSupported(Boolean(SR));
  }, []);

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isTyping]);

  // Auto-resize textarea
  useEffect(() => {
    const ta = textareaRef.current;
    if (ta) {
      ta.style.height = "auto";
      ta.style.height = Math.min(ta.scrollHeight, 120) + "px";
    }
  }, [inputText]);

  const sendMessage = useCallback((text, files) => {
    if (!text.trim() && (!files || files.length === 0)) return;

    const userMsg = {
      id: `user-${Date.now()}`,
      role: "user",
      text: text.trim(),
      files: files && files.length > 0 ? [...files] : undefined,
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMsg]);
    setInputText("");
    setAttachedFiles([]);
    setShowPrompts(false);
    setIsTyping(true);

    // Reset textarea height
    if (textareaRef.current) textareaRef.current.style.height = "auto";

    // Simulate AI thinking delay
    setTimeout(() => {
      const response = generateMockResponse(text, files);
      const assistantMsg = {
        id: `assistant-${Date.now()}`,
        role: "assistant",
        text: response.summary,
        response,
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, assistantMsg]);
      setIsTyping(false);
    }, 1200 + Math.random() * 800);
  }, []);

  const handleSend = () => sendMessage(inputText, attachedFiles);

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handlePromptClick = (prompt) => {
    if (prompt === "Start Weekly Reset") {
      weeklyReset.start();
      return;
    }
    sendMessage(prompt, []);
  };

  const handleFileChange = (e) => {
    const newFiles = Array.from(e.target.files || []);
    setAttachedFiles(prev => [...prev, ...newFiles]);
    toast(`${newFiles.length} file${newFiles.length > 1 ? "s" : ""} attached — pick a suggested action below.`);
    e.target.value = "";
  };

  const removeFile = (name) => setAttachedFiles(prev => prev.filter(f => f.name !== name));

  // File-action mock — pushes a tailored assistant message into chat
  const handleFileAction = useCallback((file, actionKey, actionLabel) => {
    setShowPrompts(false);

    // 1) User-side bubble describing the requested action on the file
    const userMsg = {
      id: `user-fa-${Date.now()}`,
      role: "user",
      text: `${actionLabel} — ${file.name}`,
      files: [file],
      timestamp: new Date(),
    };
    setMessages(prev => [...prev, userMsg]);
    setIsTyping(true);

    // 2) Simulated assistant response acknowledging the action is staged
    setTimeout(() => {
      const response = buildFileActionResponse(file, actionKey);
      const assistantMsg = {
        id: `assistant-fa-${Date.now()}`,
        role: "assistant",
        text: response.summary,
        response,
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, assistantMsg]);
      setIsTyping(false);
    }, 900 + Math.random() * 600);
  }, []);

  // Voice recognition (browser-native, no backend).
  // Captures speech, inserts the transcript into the input, and leaves
  // the user in control — Send is never triggered automatically.
  const toggleVoice = () => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;

    // Fallback when the browser doesn't support speech recognition.
    if (!SpeechRecognition) {
      toast("Voice input is not available on this device. You can type your instruction instead.");
      return;
    }

    // Toggle off — stop the active session cleanly.
    if (isRecording && recognitionRef.current) {
      recognitionRef.current.stop();
      return; // onend handler resets state
    }

    const recognition = new SpeechRecognition();
    recognition.continuous = false;     // user-controlled — single utterance per click
    recognition.interimResults = true;  // show partial transcripts live for feedback
    recognition.lang = "en-NZ";
    recognition.maxAlternatives = 1;

    recognition.onstart = () => {
      setIsRecording(true);
      setInterimTranscript("");
    };

    recognition.onresult = (event) => {
      let finalChunk = "";
      let interimChunk = "";
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        const transcript = result[0].transcript;
        if (result.isFinal) finalChunk += transcript;
        else interimChunk += transcript;
      }
      setInterimTranscript(interimChunk);

      if (finalChunk) {
        setInputText(prev => (prev ? prev.trimEnd() + " " : "") + finalChunk.trim());
      }
    };

    recognition.onerror = (event) => {
      const code = event?.error;
      if (code === "not-allowed" || code === "service-not-allowed") {
        toast("Microphone access was blocked. Enable it in your browser settings and try again.");
      } else if (code === "no-speech") {
        toast("I didn't catch anything. Tap the mic and try again.");
      } else if (code === "audio-capture") {
        toast("No microphone found. Check your device and try again.");
      } else {
        toast("Couldn't catch that. Try again or type instead.");
      }
    };

    recognition.onend = () => {
      setIsRecording(false);
      setInterimTranscript("");
      // Bring focus back to the input so the user can edit before sending.
      requestAnimationFrame(() => textareaRef.current?.focus());
    };

    recognitionRef.current = recognition;
    try {
      recognition.start();
    } catch {
      // Some browsers throw if start() is called while already started.
      setIsRecording(false);
    }
  };

  const [calmModalOpen, setCalmModalOpen] = useState(false);
  const { isCalm } = useCalmMode();
  const weeklyReset = useWeeklyReset();
  const { phase } = useLightPhase();

  return (
    <ResidenceLayout rail={<ResidenceRail currentKey="today" />} phase={phase} testid="ai-command-centre-page">
      <PageHeading
        module="today"
        room="Ask ARA"
        title="Ask ARA"
        focal="Type, speak, or hand ARA a file. It listens first, acts only with your permission."
        actions={
          <>
            {!isCalm && (
              <TextLink onClick={() => setCalmModalOpen(true)} testid="calm-mode-ai-btn">Calm mode</TextLink>
            )}
            <TextLink onClick={() => weeklyReset.open ? weeklyReset.close?.() : weeklyReset.setOpen?.(true)} testid="ask-ara-weekly-reset">Weekly reset</TextLink>
          </>
        }
      />

      <div className="flex flex-col h-[calc(100vh-260px)] min-h-[520px]" data-testid="ask-ara-column">

        {/* ─── Messages Area ─── */}
        <div className="flex-1 overflow-y-auto pt-4 pb-6" data-testid="messages-area">
          <div className="max-w-[720px] space-y-5">
            {messages.map(msg => (
              <MessageBubble key={msg.id} message={msg} />
            ))}
            {isTyping && <TypingIndicator />}
            <div ref={messagesEndRef} />
          </div>

          {/* ─── Suggested Prompts ─── */}
          {showPrompts && messages.length <= 1 && (
            <div className="max-w-[720px] mt-8" data-testid="suggested-prompts">
              <p className="caption-ara mb-3">Try asking</p>
              <div className="flex flex-wrap gap-x-6 gap-y-2">
                {SUGGESTED_PROMPTS.map(prompt => (
                  <button
                    key={prompt}
                    onClick={() => handlePromptClick(prompt)}
                    className="link-ara"
                    data-testid={`prompt-chip-${prompt.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {prompt}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* ─── Composer ─── */}
        <div className="flex-shrink-0 pt-4 pb-4 border-t" style={{ borderColor: 'var(--ara-line)' }} data-testid="composer-area">
          <div className="max-w-[720px]">
            {/* Attached files preview */}
            {attachedFiles.length > 0 && (
              <div className="mb-3" data-testid="attached-files-preview">
                <div className="flex items-center gap-1.5 mb-2">
                  <HardDriveUpload className="w-3.5 h-3.5 text-violet-500" strokeWidth={1.5} />
                  <p className="text-[11px] font-extrabold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    {attachedFiles.length} file{attachedFiles.length > 1 ? "s" : ""} ready
                  </p>
                  <span className="text-[11px] text-gray-300 dark:text-gray-600">•</span>
                  <p className="text-[11px] font-medium text-gray-400 dark:text-gray-500 italic">
                    Pick a quick action below, or add a message and Send.
                  </p>
                </div>
                <div className="space-y-2">
                  {attachedFiles.map(f => (
                    <FileAttachmentCard
                      key={f.name}
                      file={f}
                      onRemove={removeFile}
                      onAction={handleFileAction}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Listening status pill — visible whenever voice input is active */}
            {isRecording && (
              <div
                className="mb-2 inline-flex items-center gap-2 rounded-full bg-rose-50 dark:bg-rose-900/30 border border-rose-200 dark:border-rose-700/60 px-3 py-1.5 max-w-full"
                role="status"
                aria-live="polite"
                data-testid="listening-indicator"
              >
                <span className="relative flex h-2 w-2 flex-shrink-0">
                  <span className="absolute inline-flex h-full w-full rounded-full bg-rose-400 opacity-75 animate-ping" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-rose-500" />
                </span>
                <span className="text-[11px] font-extrabold text-rose-700 dark:text-rose-200 uppercase tracking-wider whitespace-nowrap">
                  Listening…
                </span>
                {interimTranscript && (
                  <span className="text-[11px] font-medium text-rose-700/80 dark:text-rose-200/80 italic truncate" data-testid="interim-transcript">
                    “{interimTranscript}”
                  </span>
                )}
                <button
                  onClick={toggleVoice}
                  className="ml-1 text-[10px] font-extrabold uppercase tracking-wider text-rose-600 dark:text-rose-300 hover:text-rose-800 dark:hover:text-rose-100 transition-colors"
                  data-testid="stop-listening-btn"
                  aria-label="Stop listening"
                >
                  Stop
                </button>
              </div>
            )}

            {/* Input row */}
            <div className="flex items-end gap-2">
              {/* File upload */}
              <button
                onClick={() => fileInputRef.current?.click()}
                className="flex-shrink-0 w-10 h-10 rounded-2xl bg-gray-100 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 flex items-center justify-center text-gray-500 hover:text-violet-600 hover:border-violet-300 dark:hover:text-violet-400 dark:hover:border-violet-600 transition-colors"
                title="Upload a school notice, bill, receipt, medical note, form or meal plan"
                data-testid="upload-file-btn"
              >
                <Paperclip className="w-5 h-5" strokeWidth={1.5} />
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept={ACCEPTED_TYPES}
                multiple
                onChange={handleFileChange}
                className="hidden"
                data-testid="file-input"
              />

              {/* Voice */}
              <button
                onClick={toggleVoice}
                className={`flex-shrink-0 w-10 h-10 rounded-2xl border flex items-center justify-center transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-violet-300 dark:focus:ring-violet-600 ${
                  isRecording
                    ? "bg-rose-100 border-rose-300 text-rose-600 dark:bg-rose-900/30 dark:border-rose-700 dark:text-rose-300 ring-2 ring-rose-300 dark:ring-rose-700 animate-pulse"
                    : voiceSupported
                      ? "bg-gray-100 dark:bg-slate-800 border-gray-200 dark:border-slate-700 text-gray-500 hover:text-violet-600 hover:border-violet-300 dark:hover:text-violet-400 dark:hover:border-violet-600 active:scale-95"
                      : "bg-gray-50 dark:bg-slate-900 border-gray-200 dark:border-slate-800 text-gray-300 dark:text-slate-600"
                }`}
                title={
                  !voiceSupported
                    ? "Voice input is not available on this device"
                    : isRecording
                      ? "Stop voice input"
                      : "Start voice input — speak your instruction"
                }
                aria-label={
                  !voiceSupported
                    ? "Voice input is not available on this device"
                    : isRecording
                      ? "Stop voice input"
                      : "Start voice input"
                }
                aria-pressed={isRecording}
                data-testid="voice-btn"
              >
                {isRecording ? <MicOff className="w-5 h-5" strokeWidth={1.5} /> : <Mic className="w-5 h-5" strokeWidth={1.5} />}
              </button>

              {/* Text input */}
              <div className="flex-1 relative">
                <textarea
                  ref={textareaRef}
                  value={inputText}
                  onChange={(e) => setInputText(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder={isRecording ? "Listening… speak your instruction" : "Ask ARA…"}
                  rows={1}
                  aria-label="Household instruction."
                  className="w-full resize-none bg-transparent px-3 py-3 text-[14.5px] focus:outline-none"
                  style={{
                    fontFamily: 'Inter, system-ui, sans-serif',
                    color: 'var(--ara-ink)',
                    borderBottom: `1px solid ${isRecording ? '#8A4B4B' : 'var(--ara-ink)'}`,
                  }}
                  data-testid="message-input"
                />
              </div>

              {/* Send */}
              <button
                onClick={handleSend}
                disabled={!inputText.trim() && attachedFiles.length === 0}
                className="flex-shrink-0 h-10 px-5 rounded-full inline-flex items-center gap-2 transition-ara disabled:opacity-40 disabled:cursor-not-allowed"
                style={{
                  background: 'var(--ara-ink)',
                  color: 'var(--ara-canvas)',
                  fontFamily: 'Inter, system-ui, sans-serif',
                  fontWeight: 500,
                  fontSize: '13px',
                }}
                title="Send"
                data-testid="send-btn"
              >
                <Send className="w-4 h-4" strokeWidth={1.5} />
                <span>Send</span>
              </button>
            </div>

            <p className="caption-ara mt-3" data-testid="composer-helper-text">
              Upload school notices, bills, receipts, medical notes, forms or documents. ARA uses mock responses for now.
            </p>
          </div>
        </div>
      </div>

      <Colophon>ARA · Concierge</Colophon>

      <CalmModeModal open={calmModalOpen} onClose={() => setCalmModalOpen(false)} />
      <WeeklyResetModal open={weeklyReset.open} onClose={weeklyReset.close} />
      <BetaFeedbackButton />
    </ResidenceLayout>
  );
}
